import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Pencil, Trash2, Plus, RotateCcw, AlertTriangle,
  Download, Undo2, Mail, RefreshCw,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatEastern, formatEasternTime, easternInputToISO } from "@/lib/utils";
import RichTextEditor from "@/components/RichTextEditor";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import { Time12Picker } from "@/components/ui/time12-picker";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { AdminShell } from "@/components/admin/AdminShell";

type BookingRow = Database["public"]["Tables"]["bookings"]["Row"];
type BlogPostRow = Database["public"]["Tables"]["blog_posts"]["Row"];
type ProductRow = Database["public"]["Tables"]["products"]["Row"];
type PostStatus = Database["public"]["Enums"]["post_status"];
type BookingStatus = Database["public"]["Enums"]["booking_status"];
type PaymentStatus = Database["public"]["Enums"]["payment_status"];

export const AdminLayout = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <AdminShell title={title}>{children}</AdminShell>
);

const StatCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="bg-panel border border-border p-6">
    <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-2">{label}</p>
    <p className="text-3xl font-display text-primary">{value}</p>
    {sub && <p className="text-xs text-muted-foreground font-body mt-1">{sub}</p>}
  </div>
);

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

// ============ DASHBOARD ============
const AdminDashboard = () => {
  const [stats, setStats] = useState({ bookings: 0, revenue: 0, customers: 0, posts: 0 });
  const [recent, setRecent] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stripeInfo, setStripeInfo] = useState<{
    mode: string; account_id?: string; business_name?: string | null;
    charges_enabled?: boolean; payouts_enabled?: boolean; error?: string;
  } | null>(null);

  useEffect(() => {
    (async () => {
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const [bk, rev, cust, posts, recentBk] = await Promise.all([
        supabase.from("bookings").select("id", { count: "exact", head: true }),
        supabase.from("bookings").select("amount_paid").gte("created_at", monthStart.toISOString()),
        supabase.from("customers").select("id", { count: "exact", head: true }),
        supabase.from("blog_posts").select("id", { count: "exact", head: true }).eq("status", "published"),
        supabase.from("bookings").select("*").order("created_at", { ascending: false }).limit(5),
      ]);

      const revenue = (rev.data ?? []).reduce((s, r) => s + Number(r.amount_paid || 0), 0);
      setStats({
        bookings: bk.count ?? 0,
        revenue,
        customers: cust.count ?? 0,
        posts: posts.count ?? 0,
      });
      setRecent(recentBk.data ?? []);
      setLoading(false);
    })();
    (async () => {
      const { data, error } = await supabase.functions.invoke("stripe-account-info");
      if (error) { setStripeInfo({ mode: "unknown", error: error.message }); return; }
      setStripeInfo(data);
    })();
  }, []);

  return (
    <AdminLayout title="DASHBOARD">
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {stripeInfo && (
            <div className="bg-panel border border-border p-4 mb-6 flex flex-wrap items-center gap-4">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Stripe</span>
              {stripeInfo.mode === "live" ? (
                <span className="px-3 py-1 text-xs font-mono uppercase border border-emerald-500/40 bg-emerald-500/15 text-emerald-400">LIVE MODE</span>
              ) : stripeInfo.mode === "test" ? (
                <span className="px-3 py-1 text-xs font-mono uppercase border border-yellow-500/40 bg-yellow-500/15 text-yellow-400">TEST MODE</span>
              ) : (
                <span className="px-3 py-1 text-xs font-mono uppercase border border-border text-muted-foreground">UNKNOWN</span>
              )}
              {stripeInfo.business_name && (
                <span className="text-sm font-body">{stripeInfo.business_name}</span>
              )}
              {stripeInfo.account_id && (
                <span className="text-xs font-mono text-muted-foreground">{stripeInfo.account_id}</span>
              )}
              {stripeInfo.charges_enabled === false && (
                <span className="flex items-center gap-1 text-xs text-red-400 font-mono">
                  <AlertTriangle className="w-3 h-3" /> Charges disabled
                </span>
              )}
              {stripeInfo.error && (
                <span className="text-xs text-red-400 font-mono">{stripeInfo.error}</span>
              )}
              <div className="ml-auto flex gap-2">
                <Button asChild variant="outline" size="sm"><Link to="/admin/stripe/sync">Sync</Link></Button>
                <Button asChild variant="outline" size="sm"><Link to="/admin/stripe/events">Events</Link></Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <StatCard label="Total Bookings" value={String(stats.bookings)} />
            <StatCard label="Revenue (MTD)" value={fmtMoney(stats.revenue)} />
            <StatCard label="Customers" value={String(stats.customers)} />
            <StatCard label="Published Posts" value={String(stats.posts)} />
          </div>
          <div className="bg-panel border border-border p-6">
            <h2 className="font-display tracking-widest mb-4">RECENT BOOKINGS</h2>
            {recent.length === 0 ? (
              <p className="text-muted-foreground font-body text-sm">No bookings yet.</p>
            ) : (
              <div className="space-y-2">
                {recent.map((b) => (
                  <div key={b.id} className="flex items-center justify-between border-b border-border pb-2 last:border-0">
                    <div>
                      <p className="font-body text-sm">{formatEastern(b.start_at)}</p>
                      <p className="text-xs text-muted-foreground font-mono uppercase">{b.status} · {b.payment_status}</p>
                    </div>
                    <p className="font-display text-primary">{fmtMoney(Number(b.total))}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
};

// ============ BOOKINGS CRM ============
type AddonLine = { name: string; option_label: string | null; quantity: number; unit_price: number };

const AdminBookings = () => {
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [paymentFilter, setPaymentFilter] = useState<string>("all");
  const [invoiceFilter, setInvoiceFilter] = useState<string>("all"); // all | sent | not_sent
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [invoiceSentSet, setInvoiceSentSet] = useState<Set<string>>(new Set());
  const [addonsByBooking, setAddonsByBooking] = useState<Map<string, AddonLine[]>>(new Map());
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const { user } = useAuth();

  // ---- Manual create dialog ----
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [studios, setStudios] = useState<{ id: string; name: string; hourly_rate: number }[]>([]);
  type AddonOpt = { id: string; label: string; price_delta: number };
  type AvailAddon = { id: string; name: string; price: number; max_quantity: number | null; options: AddonOpt[] };
  const [availableAddons, setAvailableAddons] = useState<AvailAddon[]>([]);
  // Key = `${addon_id}::${option_id ?? ''}`
  const [pickedAddons, setPickedAddons] = useState<Record<string, { addon_id: string; option_id: string | null; option_label: string | null; unit_price: number; quantity: number }>>({});
  // Currently-selected option per addon in the picker UI
  const [optionPick, setOptionPick] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState({
    studio_id: "",
    date: "",
    start_time: "10:00",
    hours: "2",
    customer_name: "",
    customer_email: "",
    total: "",
    status: "confirmed" as BookingStatus,
    payment_status: "paid" as PaymentStatus,
    notes: "",
    internal_notes: "",
  });

  // Customer autocomplete
  type CustomerLite = { id: string; full_name: string | null; email: string; phone: string | null };
  const [allCustomers, setAllCustomers] = useState<CustomerLite[]>([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [showCustomerList, setShowCustomerList] = useState(false);

  useEffect(() => {
    if (!createOpen) return;
    supabase
      .from("customers")
      .select("id,full_name,email,phone")
      .order("created_at", { ascending: false })
      .limit(2000)
      .then(({ data }) => setAllCustomers((data ?? []) as CustomerLite[]));
  }, [createOpen]);

  const customerMatches = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return [];
    return allCustomers
      .filter(
        (c) =>
          c.email.toLowerCase().includes(q) ||
          (c.full_name ?? "").toLowerCase().includes(q) ||
          (c.phone ?? "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [allCustomers, customerQuery]);

  const pickCustomer = (c: CustomerLite) => {
    setDraft((d) => ({
      ...d,
      customer_name: c.full_name ?? "",
      customer_email: c.email,
    }));
    setCustomerQuery("");
    setShowCustomerList(false);
  };

  useEffect(() => {
    supabase
      .from("studios")
      .select("id,name,hourly_rate")
      .eq("active", true)
      .order("display_order")
      .then(({ data }) => setStudios(data ?? []));
  }, []);

  // Load add-ons applicable to the chosen studio (globals + studio-specific + linked).
  useEffect(() => {
    (async () => {
      if (!createOpen) return;
      // Globals: not tied to a studio or product, and not linked via join tables.
      const { data: globals } = await supabase
        .from("addons").select("id,name,price,max_quantity")
        .eq("active", true).is("product_id", null).is("studio_id", null).order("display_order");
      const globalIds = (globals ?? []).map(g => g.id);
      let trueGlobals = globals ?? [];
      if (globalIds.length) {
        const [{ data: ls }, { data: lp }] = await Promise.all([
          supabase.from("addon_studios").select("addon_id").in("addon_id", globalIds),
          supabase.from("addon_products").select("addon_id").in("addon_id", globalIds),
        ]);
        const assigned = new Set<string>([
          ...((ls ?? []).map((x: any) => x.addon_id)),
          ...((lp ?? []).map((x: any) => x.addon_id)),
        ]);
        trueGlobals = (globals ?? []).filter(g => !assigned.has(g.id));
      }
      let studioScoped: any[] = [];
      if (draft.studio_id) {
        const { data: legacy } = await supabase
          .from("addons").select("id,name,price,max_quantity")
          .eq("active", true).eq("studio_id", draft.studio_id).order("display_order");
        const { data: links } = await supabase
          .from("addon_studios").select("addon_id").eq("studio_id", draft.studio_id);
        const linkIds = (links ?? []).map((x: any) => x.addon_id);
        let linked: any[] = [];
        if (linkIds.length) {
          const { data } = await supabase
            .from("addons").select("id,name,price,max_quantity")
            .in("id", linkIds).eq("active", true).order("display_order");
          linked = data ?? [];
        }
        studioScoped = [...(legacy ?? []), ...linked];
      }
      const seen = new Set<string>();
      const mergedBase = [...trueGlobals, ...studioScoped].filter(a => {
        if (seen.has(a.id)) return false;
        seen.add(a.id);
        return true;
      });
      // Load options for all merged addons
      const ids = mergedBase.map(a => a.id);
      let optionsByAddon = new Map<string, AddonOpt[]>();
      if (ids.length) {
        const { data: opts } = await supabase
          .from("addon_options")
          .select("id,addon_id,label,price_delta,display_order")
          .in("addon_id", ids)
          .order("display_order");
        (opts ?? []).forEach((o: any) => {
          const arr = optionsByAddon.get(o.addon_id) ?? [];
          arr.push({ id: o.id, label: o.label, price_delta: Number(o.price_delta) || 0 });
          optionsByAddon.set(o.addon_id, arr);
        });
      }
      setAvailableAddons(mergedBase.map(a => ({ ...a, options: optionsByAddon.get(a.id) ?? [] })));
    })();
  }, [createOpen, draft.studio_id]);

  // Default quantity 1 for backdrop add-ons when they become available
  useEffect(() => {
    if (!availableAddons.length) return;
    setPickedAddons(prev => {
      const next = { ...prev };
      let changed = false;
      availableAddons.forEach(a => {
        if (!a.name.toLowerCase().includes("backdrop")) return;
        const hasOptions = a.options.length > 0;
        const selOpt = hasOptions ? a.options[0] : null;
        const key = `${a.id}::${selOpt?.id ?? ""}`;
        if (!(key in next)) {
          next[key] = {
            addon_id: a.id,
            option_id: selOpt?.id ?? null,
            option_label: selOpt?.label ?? null,
            unit_price: Number(a.price) + (selOpt?.price_delta ?? 0),
            quantity: 1,
          };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [availableAddons]);

  const addonsTotal = useMemo(
    () => Object.values(pickedAddons).reduce((s, l) => s + l.unit_price * l.quantity, 0),
    [pickedAddons],
  );

  const resetDraft = () =>
  {
    setDraft({
      studio_id: "", date: "", start_time: "10:00", hours: "2",
      customer_name: "", customer_email: "", total: "",
      status: "confirmed", payment_status: "paid",
      notes: "", internal_notes: "",
    });
    setPickedAddons({});
    setOptionPick({});
    setCustomerQuery("");
    setShowCustomerList(false);
  };

  const createBooking = async () => {
    if (!draft.studio_id) return toast.error("Pick a studio");
    if (!draft.date) return toast.error("Pick a date");
    const hours = Number(draft.hours);
    if (!hours || hours <= 0) return toast.error("Hours must be greater than 0");
    // Treat manual booking date/time as Eastern Time wall clock.
    const startISO = easternInputToISO(`${draft.date}T${draft.start_time || "00:00"}`);
    if (!startISO) return toast.error("Invalid date/time");
    const start = new Date(startISO);
    const end = new Date(start.getTime() + hours * 3600_000);
    const endISO = end.toISOString();

    setCreating(true);
    try {
      // Resolve customer (optional)
      let customer_id: string | null = null;
      const email = draft.customer_email.trim().toLowerCase();
      if (email) {
        const { data: existing } = await supabase
          .from("customers").select("id").eq("email", email).maybeSingle();
        if (existing) {
          customer_id = existing.id;
        } else {
          const { data: ins, error: insErr } = await supabase
            .from("customers")
            .insert({ email, full_name: draft.customer_name.trim() || null })
            .select("id").single();
          if (insErr) throw insErr;
          customer_id = ins.id;
        }
      }

      const studio = studios.find((s) => s.id === draft.studio_id);
      const hours = (new Date(endISO).getTime() - new Date(startISO).getTime()) / 3600_000;
      const baseComputed = studio ? Number((studio.hourly_rate * hours).toFixed(2)) : 0;
      // draft.total = OPTIONAL base override (excludes add-ons). Final total
      // is always (base ± override) + addonsTotal, so add-ons are never
      // silently dropped from a manual booking.
      const baseOverride = draft.total !== "" ? Number(draft.total) : NaN;
      const base = !Number.isNaN(baseOverride) ? baseOverride : baseComputed;
      const total = Number((base + addonsTotal).toFixed(2));
      const subtotal = total;
      const amount_paid = draft.payment_status === "paid" ? total
        : draft.payment_status === "partial" ? Number((total / 2).toFixed(2)) : 0;

      const { data: inserted, error } = await supabase.from("bookings").insert({
        studio_id: draft.studio_id,
        start_at: startISO,
        end_at: endISO,
        customer_id,
        status: draft.status,
        payment_status: draft.payment_status,
        subtotal,
        total,
        amount_paid,
        notes: draft.notes || null,
        internal_notes: draft.internal_notes || null,
      }).select("id").single();
      if (error) throw error;

      // Insert chosen add-ons (with optional option selection)
      const addonRows = Object.values(pickedAddons)
        .filter(l => l.quantity > 0)
        .map(l => ({
          booking_id: inserted!.id,
          addon_id: l.addon_id,
          option_id: l.option_id,
          option_label: l.option_label,
          quantity: l.quantity,
          unit_price: l.unit_price,
        }));
      if (addonRows.length) {
        const { error: aErr } = await supabase.from("booking_addons").insert(addonRows);
        if (aErr) toast.error(`Booking saved, but add-on insert failed: ${aErr.message}`);
      }

      toast.success("Booking created");

      // Auto-send the booking confirmation email so manually-created
      // bookings give the customer the same receipt as Stripe checkout.
      if (customer_id && draft.customer_email.trim()) {
        try {
          const { data: emailData, error: emailErr } = await supabase.functions.invoke(
            "resend-booking-confirmation",
            { body: { booking_id: inserted!.id } },
          );
          if (emailErr || (emailData as any)?.error) {
            toast.error(
              `Booking created, but confirmation email failed: ${emailErr?.message || (emailData as any)?.error}`,
            );
          } else {
            toast.success("Confirmation email sent to customer");
          }
        } catch (e: any) {
          toast.error(`Booking created, but confirmation email failed: ${e?.message ?? e}`);
        }
      }

      // Auto-send a Stripe-hosted invoice for any outstanding balance so the
      // customer gets a Pay link without admin clicking through.
      const balance = Number((total - amount_paid).toFixed(2));
      if (balance > 0 && customer_id && draft.customer_email.trim()) {
        try {
          const { data: invData, error: invErr } = await supabase.functions.invoke(
            "stripe-send-invoice",
            { body: { booking_id: inserted!.id } },
          );
          if (invErr || (invData as any)?.error) {
            toast.error(`Booking created, but Stripe invoice failed: ${invErr?.message || (invData as any)?.error}`);
          } else {
            toast.success("Stripe invoice emailed to customer");
          }
        } catch (e: any) {
          toast.error(`Booking created, but Stripe invoice failed: ${e?.message ?? e}`);
        }
      }

      setCreateOpen(false);
      resetDraft();
      load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to create booking");
    } finally {
      setCreating(false);
    }
  };

  const load = async () => {
    setLoading(true);
    let q = supabase.from("bookings").select("*").order("start_at", { ascending: false });
    if (statusFilter !== "all") q = q.eq("status", statusFilter as BookingStatus);
    if (paymentFilter !== "all") q = q.eq("payment_status", paymentFilter as PaymentStatus);
    if (dateFrom) q = q.gte("start_at", new Date(dateFrom).toISOString());
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      q = q.lte("start_at", to.toISOString());
    }
    const { data, error } = await q;
    if (error) toast.error(error.message);
    const list = (data ?? []) as BookingRow[];
    // Invoice-sent set (used both for the UI badge & invoice filter)
    const ids = list.map((r) => r.id);
    let sent = new Set<string>();
    if (ids.length) {
      const { data: el } = await supabase
        .from("email_send_log")
        .select("status,metadata,template_name")
        .in("template_name", ["booking-invoice-link"]);
      (el ?? []).forEach((e: any) => {
        const bid = e?.metadata?.booking_id;
        if (bid && ids.includes(bid) && e.status === "sent") sent.add(bid);
      });
    }
    setInvoiceSentSet(sent);
    // Add-on lines (with the chosen option, e.g. backdrop colour) for the rows on screen
    const addonMap = new Map<string, AddonLine[]>();
    if (ids.length) {
      const { data: al } = await supabase
        .from("booking_addons")
        .select("booking_id,quantity,unit_price,option_label,addons(name)")
        .in("booking_id", ids);
      (al ?? []).forEach((a: any) => {
        const arr = addonMap.get(a.booking_id) ?? [];
        arr.push({
          name: a.addons?.name ?? "Add-on",
          option_label: a.option_label ?? null,
          quantity: Number(a.quantity || 1),
          unit_price: Number(a.unit_price || 0),
        });
        addonMap.set(a.booking_id, arr);
      });
    }
    setAddonsByBooking(addonMap);
    const filtered = invoiceFilter === "all"
      ? list
      : list.filter((b) => (invoiceFilter === "sent" ? sent.has(b.id) : !sent.has(b.id)));
    setRows(filtered);
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter, paymentFilter, invoiceFilter, dateFrom, dateTo]);

  const updateBooking = async (id: string, patch: Partial<BookingRow>) => {
    const { error } = await supabase.from("bookings").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Booking updated");
    load();
  };

  const deleteBooking = async (b: BookingRow) => {
    const paid = Number(b.amount_paid) > 0;
    const warning = paid
      ? `This booking has ${fmtMoney(Number(b.amount_paid))} paid. Deleting REMOVES the record entirely — it will NOT issue a refund.\n\nType DELETE to confirm permanent removal:`
      : `Permanently delete this booking? This cannot be undone.\n\nType DELETE to confirm:`;
    const confirmText = prompt(warning, "");
    if (confirmText !== "DELETE") return;
    try {
      // Best-effort: clean up Stripe / Google Calendar before deleting the row
      try {
        await supabase.functions.invoke("cancel-booking", {
          body: { booking_id: b.id, reason: "deleted by admin", notify_customer: false },
        });
      } catch {
        // ignore — booking may already be cancelled
      }
      const { error } = await supabase.from("bookings").delete().eq("id", b.id);
      if (error) throw error;
      await supabase.from("admin_audit_log").insert({
        action: "delete_booking",
        entity_type: "booking",
        entity_id: b.id,
        actor_id: user?.id,
        after: {
          total: b.total,
          amount_paid: b.amount_paid,
          status: b.status,
          payment_status: b.payment_status,
        },
      });
      toast.success("Booking deleted");
      load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to delete booking");
    }
  };

  const sendPreviewToMe = async (b: BookingRow) => {
    if (!user?.email) return toast.error("No admin email on file");
    setSendingId(b.id);
    try {
      // Fetch studio + customer for accurate template data
      const [{ data: studio }, { data: customer }] = await Promise.all([
        supabase.from("studios").select("name").eq("id", b.studio_id).maybeSingle(),
        b.customer_id
          ? supabase.from("customers").select("full_name, email").eq("id", b.customer_id).maybeSingle()
          : Promise.resolve({ data: null as { full_name: string | null; email: string } | null }),
      ]);
      const fmt = (iso: string) => formatEastern(iso, { weekday: "long" });
      const { error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "booking-confirmation",
          recipientEmail: user.email,
          // Unique key per admin+booking so repeated previews aren't blocked
          idempotencyKey: `booking-confirm-preview-${b.id}-${Date.now()}`,
          templateData: {
            customerName: customer?.full_name || user.email,
            studioName: studio?.name || "Studio",
            startAt: fmt(b.start_at),
            endAt: fmt(b.end_at),
            totalPaid: Number(b.total).toFixed(2),
            bookingId: b.id,
          },
        },
      });
      if (error) throw error;
      toast.success(`Preview sent to ${user.email}`);
    } catch (e: any) {
      toast.error(e?.message || "Failed to send preview");
    } finally {
      setSendingId(null);
    }
  };

  const resendToCustomer = async (b: BookingRow) => {
    if (!confirm("Re-send the booking confirmation email to the customer for this booking?")) return;
    setResendingId(b.id);
    try {
      const { data, error } = await supabase.functions.invoke(
        "resend-booking-confirmation",
        { body: { booking_id: b.id } },
      );
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`Resent to ${(data as any)?.recipient ?? "customer"}`);
    } catch (e: any) {
      toast.error(e?.message || "Failed to resend");
    } finally {
      setResendingId(null);
    }
  };

  const csvEscape = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const ids = rows.map((r) => r.id);
      const [{ data: addons }, { data: studiosAll }, { data: customersAll }, { data: emails }] = await Promise.all([
        ids.length
          ? supabase.from("booking_addons").select("booking_id,quantity,unit_price,option_label,addons(name)").in("booking_id", ids)
          : Promise.resolve({ data: [] as any[] }),
        supabase.from("studios").select("id,name"),
        supabase.from("customers").select("id,full_name,email,phone"),
        ids.length
          ? supabase.from("email_send_log")
              .select("template_name,status,metadata")
              .in("template_name", ["booking-confirmation", "booking-invoice-link"])
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const studioMap = new Map((studiosAll ?? []).map((s: any) => [s.id, s.name]));
      const custMap = new Map((customersAll ?? []).map((c: any) => [c.id, c]));
      const addonsByBooking = new Map<string, any[]>();
      (addons ?? []).forEach((a: any) => {
        const arr = addonsByBooking.get(a.booking_id) ?? [];
        arr.push(a);
        addonsByBooking.set(a.booking_id, arr);
      });
      const invoiceSentByBooking = new Set<string>();
      (emails ?? []).forEach((e: any) => {
        const bid = e?.metadata?.booking_id;
        if (bid && e.template_name === "booking-invoice-link" && e.status === "sent") {
          invoiceSentByBooking.add(bid);
        }
      });

      const header = [
        "booking_id", "status", "payment_status", "studio", "start_at", "end_at",
        "customer_name", "customer_email", "customer_phone",
        "subtotal", "total", "amount_paid",
        "stripe_payment_intent", "stripe_session_id",
        "addons", "addons_total", "invoice_emailed",
        "created_at", "notes",
      ];
      const lines = [header.join(",")];
      for (const b of rows) {
        const cust: any = b.customer_id ? custMap.get(b.customer_id) : null;
        const addonRows = addonsByBooking.get(b.id) ?? [];
        const addonsStr = addonRows.map((a) => {
          const name = a.addons?.name ?? "addon";
          const label = a.option_label ? `${name} (${a.option_label})` : name;
          return `${a.quantity}× ${label} @ $${Number(a.unit_price).toFixed(2)}`;
        }).join("; ");
        const addonsTotal = addonRows.reduce((s, a) => s + Number(a.unit_price) * Number(a.quantity || 1), 0);
        lines.push([
          b.id, b.status, b.payment_status, studioMap.get(b.studio_id) ?? b.studio_id,
          b.start_at, b.end_at,
          cust?.full_name ?? "", cust?.email ?? "", cust?.phone ?? "",
          Number(b.subtotal).toFixed(2), Number(b.total).toFixed(2), Number(b.amount_paid).toFixed(2),
          b.stripe_payment_intent ?? "", (b as any).stripe_checkout_session_id ?? "",
          addonsStr, addonsTotal.toFixed(2),
          invoiceSentByBooking.has(b.id) ? "yes" : "no",
          b.created_at, b.notes ?? "",
        ].map(csvEscape).join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${rows.length} bookings`);
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="BOOKINGS">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-muted-foreground font-body text-sm">{rows.length} bookings</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 font-body" aria-label="From date" />
          <span className="text-muted-foreground text-xs">→</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 font-body" aria-label="To date" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 font-body"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
          <Select value={paymentFilter} onValueChange={setPaymentFilter}>
            <SelectTrigger className="w-44 font-body"><SelectValue placeholder="Payment" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payments</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
          <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
            <SelectTrigger className="w-44 font-body"><SelectValue placeholder="Invoice" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any invoice status</SelectItem>
              <SelectItem value="sent">Invoice emailed</SelectItem>
              <SelectItem value="not_sent">Invoice NOT emailed</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportCsv} variant="outline" disabled={exporting || rows.length === 0} className="font-body">
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Export CSV
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="font-body">
            <Plus className="w-4 h-4 mr-1" /> New booking
          </Button>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetDraft(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New booking</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Studio</Label>
              <Select value={draft.studio_id} onValueChange={(v) => setDraft({ ...draft, studio_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select studio" /></SelectTrigger>
                <SelectContent>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} — ${s.hourly_rate}/hr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-3">
                <Label>Date (Eastern Time)</Label>
                <DateTimePicker
                  mode="date"
                  value={draft.date}
                  onChange={(v) => setDraft({ ...draft, date: v })}
                />
              </div>
              <div>
                <Label>Start time (ET)</Label>
                <Time12Picker
                  value={draft.start_time}
                  onChange={(v) => setDraft({ ...draft, start_time: v })}
                />
              </div>
              <div className="col-span-2">
                <Label>Hours</Label>
                <Select
                  value={draft.hours}
                  onValueChange={(v) => setDraft({ ...draft, hours: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select hours" /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12].map((h) => (
                      <SelectItem key={h} value={String(h)}>{h} {h === 1 ? "hour" : "hours"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Search existing customer</Label>
              <div className="relative">
                <Input
                  placeholder="Type name, email, or phone…"
                  value={customerQuery}
                  onChange={(e) => { setCustomerQuery(e.target.value); setShowCustomerList(true); }}
                  onFocus={() => setShowCustomerList(true)}
                  onBlur={() => setTimeout(() => setShowCustomerList(false), 150)}
                />
                {showCustomerList && customerMatches.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-popover border border-border max-h-60 overflow-auto shadow-lg">
                    {customerMatches.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-panel border-b border-border last:border-0"
                      >
                        <div className="font-body">{c.full_name || <span className="italic text-muted-foreground">(no name)</span>}</div>
                        <div className="font-mono text-xs text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Pick a match to auto-fill, or just type a new email/name below to create a customer.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Customer name</Label>
                <Input value={draft.customer_name}
                  onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })} />
              </div>
              <div>
                <Label>Customer email</Label>
                <Input type="email" value={draft.customer_email}
                  onChange={(e) => setDraft({ ...draft, customer_email: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Base override ($)</Label>
                <Input type="number" step="0.01" placeholder="auto (hours × rate)"
                  value={draft.total}
                  onChange={(e) => setDraft({ ...draft, total: e.target.value })} />
                <p className="text-[10px] text-muted-foreground font-mono mt-1">Add-ons are always added on top.</p>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v as BookingStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment</Label>
                <Select value={draft.payment_status} onValueChange={(v) => setDraft({ ...draft, payment_status: v as PaymentStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes (visible)</Label>
              <Textarea rows={2} value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </div>
            <div>
              <Label>Internal notes</Label>
              <Textarea rows={2} value={draft.internal_notes}
                onChange={(e) => setDraft({ ...draft, internal_notes: e.target.value })} />
            </div>
            <div>
              <Label>Add-ons</Label>
              {availableAddons.length === 0 ? (
                <p className="text-xs text-muted-foreground font-body mt-1">
                  {draft.studio_id ? "No add-ons available for this studio." : "Select a studio to see add-ons."}
                </p>
              ) : (
                <div className="border border-border divide-y divide-border max-h-72 overflow-y-auto">
                  {availableAddons.map((a) => {
                    const hasOptions = a.options.length > 0;
                    const max = a.max_quantity ?? 99;
                    const selOptId = optionPick[a.id] ?? (hasOptions ? a.options[0].id : "");
                    const selOpt = hasOptions ? a.options.find(o => o.id === selOptId) ?? a.options[0] : null;
                    const key = `${a.id}::${selOpt?.id ?? ""}`;
                    const line = pickedAddons[key];
                    const q = line?.quantity ?? 0;
                    const unitPrice = Number(a.price) + (selOpt?.price_delta ?? 0);
                    const setQty = (next: number) => {
                      const n = Math.max(0, Math.min(max, next));
                      setPickedAddons(p => {
                        const copy = { ...p };
                        if (n === 0) { delete copy[key]; return copy; }
                        copy[key] = {
                          addon_id: a.id,
                          option_id: selOpt?.id ?? null,
                          option_label: selOpt?.label ?? null,
                          unit_price: unitPrice,
                          quantity: n,
                        };
                        return copy;
                      });
                    };
                    return (
                      <div key={a.id} className="flex items-center justify-between gap-2 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-body truncate">{a.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">${unitPrice.toFixed(2)}{selOpt?.price_delta ? ` (base $${Number(a.price).toFixed(2)} + $${selOpt.price_delta.toFixed(2)})` : ""}</div>
                          {hasOptions && (
                            <Select value={selOpt!.id} onValueChange={(v) => setOptionPick(o => ({ ...o, [a.id]: v }))}>
                              <SelectTrigger className="h-7 text-xs mt-1 w-full max-w-[260px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {a.options.map(o => (
                                  <SelectItem key={o.id} value={o.id}>
                                    {o.label}{o.price_delta ? ` (+$${o.price_delta.toFixed(2)})` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {hasOptions && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(pickedAddons)
                                .filter(([k, l]) => l.addon_id === a.id && k !== key)
                                .map(([k, l]) => (
                                  <span key={k} className="text-[10px] font-mono bg-panel border border-border px-1.5 py-0.5">
                                    {l.option_label} ×{l.quantity}
                                    <button type="button" className="ml-1 text-destructive" onClick={() => setPickedAddons(p => { const c = { ...p }; delete c[k]; return c; })}>×</button>
                                  </span>
                                ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0"
                            onClick={() => setQty(q - 1)}>−</Button>
                          <span className="w-6 text-center font-mono text-sm">{q}</span>
                          <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0"
                            onClick={() => setQty(q + 1)}>+</Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {(() => {
                const studio = studios.find(s => s.id === draft.studio_id);
                const hrs = Number(draft.hours) || 0;
                const baseAuto = studio ? Number((studio.hourly_rate * hrs).toFixed(2)) : 0;
                const baseOverride = draft.total !== "" ? Number(draft.total) : NaN;
                const base = !Number.isNaN(baseOverride) ? baseOverride : baseAuto;
                const grand = Number((base + addonsTotal).toFixed(2));
                return (
                  <div className="mt-3 border-t border-border pt-2 grid grid-cols-3 gap-2 text-xs font-mono">
                    <div className="text-muted-foreground">Base: ${base.toFixed(2)}</div>
                    <div className="text-muted-foreground">Add-ons: ${addonsTotal.toFixed(2)}</div>
                    <div className="text-right text-primary">Total: ${grand.toFixed(2)}</div>
                  </div>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createBooking} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Create booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6">
          <p className="text-muted-foreground font-body text-sm">No bookings match.</p>
        </div>
      ) : (
        <div className="bg-panel border border-border overflow-hidden">
          {rows.map((b, i) => (
            <div key={b.id} className={`p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 md:items-center ${i < rows.length - 1 ? "border-b border-border" : ""}`}>
              <div>
                <p className="font-body text-sm">{formatEastern(b.start_at)} → {formatEasternTime(b.end_at)}</p>
                {(addonsByBooking.get(b.id) ?? []).length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {(addonsByBooking.get(b.id) ?? []).map((a, idx) => (
                      <p key={idx} className="text-xs font-mono text-muted-foreground">
                        <span className="text-foreground">{a.name}</span>
                        {a.option_label && <span className="text-primary"> — {a.option_label}</span>}
                        {" "}×{a.quantity} · {fmtMoney(a.unit_price * a.quantity)}
                      </p>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground font-mono">{b.notes || "—"}</p>
                <Link to={`/admin/bookings/${b.id}`} className="text-xs text-primary font-mono hover:underline">View detail →</Link>
              </div>
              <p className="font-display text-primary">{fmtMoney(Number(b.total))}</p>
              <Select value={b.status} onValueChange={(v) => updateBooking(b.id, { status: v as BookingStatus })}>
                <SelectTrigger className="w-36 h-8 text-xs font-mono uppercase"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              <Select value={b.payment_status} onValueChange={(v) => updateBooking(b.id, { payment_status: v as PaymentStatus })}>
                <SelectTrigger className="w-32 h-8 text-xs font-mono uppercase"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              {b.status !== "cancelled" && b.status !== "refunded" && (
                <Button variant="outline" size="sm" onClick={async () => {
                  const hours = Math.max(0, Math.floor((new Date(b.start_at).getTime() - Date.now()) / 36e5));
                  const reason = prompt(`Cancel this booking?\n\nHours until start: ${hours}\nAmount paid: ${fmtMoney(Number(b.amount_paid))}\n\nThe configured refund policy will be applied automatically. Optional reason for the customer:`, "");
                  if (reason === null) return;
                  const { data, error } = await supabase.functions.invoke("cancel-booking", {
                    body: { booking_id: b.id, reason, notify_customer: true },
                  });
                  if (error || (data as any)?.error) return toast.error(error?.message || (data as any)?.error);
                  toast.success(`Cancelled — refunded ${fmtMoney(Number((data as any)?.refund_amount ?? 0))} (policy: ${(data as any)?.policy ?? "none"})`);
                  load();
                }} className="font-body text-xs text-destructive hover:text-destructive">
                  <Undo2 className="w-3 h-3 mr-1" /> Cancel
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={sendingId === b.id}
                onClick={() => sendPreviewToMe(b)}
                className="font-body text-xs"
                title="Send a booking-confirmation preview to your admin email"
              >
                {sendingId === b.id ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Mail className="w-3 h-3 mr-1" />
                )}
                Email me
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={resendingId === b.id}
                onClick={() => resendToCustomer(b)}
                className="font-body text-xs"
                title="Re-send booking confirmation to the customer on file"
              >
                {resendingId === b.id ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <RotateCcw className="w-3 h-3 mr-1" />
                )}
                Resend
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => deleteBooking(b)}
                className="font-body text-xs text-destructive hover:text-destructive"
                title="Permanently delete this booking"
              >
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// ============ BLOG EDITOR ============
const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

type PostDraft = {
  id?: string;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  cover_image: string;
  status: PostStatus;
};

const emptyPost: PostDraft = { title: "", slug: "", excerpt: "", body: "", cover_image: "", status: "draft" };

const AdminBlog = () => {
  const [posts, setPosts] = useState<BlogPostRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("blog_posts").select("*").order("updated_at", { ascending: false });
    if (error) toast.error(error.message);
    setPosts(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id: string) => {
    if (!confirm("Delete this post?")) return;
    const { error } = await supabase.from("blog_posts").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Post deleted");
    load();
  };

  return (
    <AdminLayout title="BLOG POSTS">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground font-body text-sm">{posts.length} posts</p>
        <Link to="/admin/blog/new">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6">
            <Plus className="w-4 h-4 mr-1" /> New Post
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : posts.length === 0 ? (
        <div className="bg-panel border border-border p-6">
          <p className="text-muted-foreground font-body text-sm">No posts yet. Click “New Post” to get started.</p>
        </div>
      ) : (
        <div className="bg-panel border border-border overflow-hidden">
          {posts.map((p, i) => (
            <div key={p.id} className={`p-4 flex items-center justify-between gap-3 ${i < posts.length - 1 ? "border-b border-border" : ""}`}>
              <div className="min-w-0">
                <h3 className="font-body font-medium text-sm truncate">{p.title}</h3>
                <p className="text-xs text-muted-foreground font-mono mt-1 uppercase">/{p.slug} · {p.status}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to={`/admin/blog/${p.id}`}>
                  <Button variant="outline" size="sm" className="font-body text-xs">
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={() => del(p.id)} className="font-body text-xs text-destructive hover:text-destructive">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// ============ PRODUCTS CRM ============
type ProductDraft = {
  id?: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  compare_at_price: number | null;
  category: string;
  active: boolean;
  image_url: string;
};
const emptyProduct: ProductDraft = {
  name: "", slug: "", description: "", price: 0,
  compare_at_price: null, category: "", active: true, image_url: "",
};

const AdminProducts = () => {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ProductDraft>(emptyProduct);
  const [saving, setSaving] = useState(false);
  // Per-product add-ons (attributes)
  type AddonRow = Database["public"]["Tables"]["addons"]["Row"];
  const [pAddons, setPAddons] = useState<AddonRow[]>([]);
  const [pAddonsLoading, setPAddonsLoading] = useState(false);
  const [newAddon, setNewAddon] = useState<{ name: string; price: string; max_quantity: string }>({ name: "", price: "", max_quantity: "" });

  const loadProductAddons = async (productId: string) => {
    setPAddonsLoading(true);
    const { data, error } = await supabase
      .from("addons")
      .select("*")
      .eq("product_id", productId)
      .order("display_order")
      .order("created_at");
    if (error) toast.error(error.message);
    setPAddons(data ?? []);
    setPAddonsLoading(false);
  };

  const addProductAddon = async () => {
    if (!draft.id) return toast.error("Save the product first");
    const name = newAddon.name.trim();
    const price = Number(newAddon.price);
    if (!name) return toast.error("Add-on name required");
    if (!Number.isFinite(price) || price < 0) return toast.error("Valid price required");
    const maxQ = newAddon.max_quantity ? Math.max(1, parseInt(newAddon.max_quantity, 10)) : null;
    const { error } = await supabase.from("addons").insert({
      name, price, product_id: draft.id, max_quantity: maxQ, pricing_unit: "flat", active: true,
      display_order: pAddons.length,
    });
    if (error) return toast.error(error.message);
    setNewAddon({ name: "", price: "", max_quantity: "" });
    loadProductAddons(draft.id);
  };

  const toggleProductAddon = async (a: AddonRow) => {
    const { error } = await supabase.from("addons").update({ active: !a.active }).eq("id", a.id);
    if (error) return toast.error(error.message);
    if (draft.id) loadProductAddons(draft.id);
  };

  const deleteProductAddon = async (id: string) => {
    if (!confirm("Delete this add-on?")) return;
    const { error } = await supabase.from("addons").delete().eq("id", id);
    if (error) return toast.error(error.message);
    if (draft.id) loadProductAddons(draft.id);
  };

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("products").select("*").order("display_order").order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(emptyProduct); setOpen(true); };
  const openEdit = (p: ProductRow) => {
    const imgs = Array.isArray(p.images) ? (p.images as unknown[]) : [];
    setDraft({
      id: p.id, name: p.name, slug: p.slug,
      description: p.description ?? "", price: Number(p.price),
      compare_at_price: p.compare_at_price ? Number(p.compare_at_price) : null,
      category: p.category ?? "", active: p.active,
      image_url: typeof imgs[0] === "string" ? (imgs[0] as string) : "",
    });
    loadProductAddons(p.id);
    setOpen(true);
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Name required");
    const slug = draft.slug.trim() || slugify(draft.name);
    setSaving(true);
    const payload = {
      name: draft.name.trim(),
      slug,
      description: draft.description || null,
      price: draft.price,
      compare_at_price: draft.compare_at_price,
      category: draft.category || null,
      active: draft.active,
      images: draft.image_url ? [draft.image_url] : [],
    };
    const { error } = draft.id
      ? await supabase.from("products").update(payload).eq("id", draft.id)
      : await supabase.from("products").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(draft.id ? "Product updated" : "Product created");
    setOpen(false);
    load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete this product?")) return;
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Product deleted");
    load();
  };

  return (
    <AdminLayout title="PRODUCTS">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground font-body text-sm">{rows.length} products</p>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6">
          <Plus className="w-4 h-4 mr-1" /> Add Product
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6">
          <p className="text-muted-foreground font-body text-sm">No products yet.</p>
        </div>
      ) : (
        <div className="bg-panel border border-border overflow-hidden">
          {rows.map((p, i) => (
            <div key={p.id} className={`p-4 flex items-center justify-between gap-3 ${i < rows.length - 1 ? "border-b border-border" : ""}`}>
              <div className="min-w-0">
                <h3 className="font-body font-medium text-sm truncate">{p.name}</h3>
                <p className="text-xs text-primary font-mono mt-1">{fmtMoney(Number(p.price))} · {p.active ? "Active" : "Hidden"}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={async () => {
                  toast.loading("Syncing to Stripe…", { id: `sync-${p.id}` });
                  const { data, error } = await supabase.functions.invoke("stripe-sync-product", { body: { product_id: p.id } });
                  if (error || data?.error) return toast.error(error?.message || data?.error, { id: `sync-${p.id}` });
                  toast.success(p.stripe_product_id ? "Stripe updated" : "Synced to Stripe", { id: `sync-${p.id}` });
                  load();
                }} className="font-body text-xs">
                  <RefreshCw className="w-3 h-3 mr-1" /> {p.stripe_product_id ? "Resync" : "Sync"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} className="font-body text-xs">
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => del(p.id)} className="font-body text-xs text-destructive hover:text-destructive">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-widest">{draft.id ? "EDIT PRODUCT" : "NEW PRODUCT"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value, slug: draft.slug || slugify(e.target.value) })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Slug</Label>
              <Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Price (USD)</Label>
                <Input type="number" step="0.01" value={draft.price} onChange={(e) => setDraft({ ...draft, price: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Compare-at</Label>
                <Input type="number" step="0.01" value={draft.compare_at_price ?? ""} onChange={(e) => setDraft({ ...draft, compare_at_price: e.target.value === "" ? null : Number(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Category</Label>
              <Input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="apparel, accessories, …" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Image URL</Label>
              <Input value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} placeholder="https://…" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Description</Label>
              <Textarea rows={4} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
              <Label className="font-mono text-xs uppercase tracking-widest">Active (visible on shop)</Label>
            </div>

            {draft.id && (
              <div className="pt-4 border-t border-border">
                <Label className="font-mono text-xs uppercase tracking-widest mb-2 block">Attributes / Add-ons</Label>
                <p className="text-xs text-muted-foreground mb-3">Optional extras customers can add when booking this product (e.g. Backdrop color +$10, Throne chair +$65).</p>
                {pAddonsLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
                ) : (
                  <div className="space-y-2 mb-3">
                    {pAddons.length === 0 && (
                      <p className="text-xs text-muted-foreground">No add-ons yet.</p>
                    )}
                    {pAddons.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 border border-border p-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-body truncate">{a.name}</p>
                          <p className="text-xs text-primary font-mono">{fmtMoney(Number(a.price))}{a.max_quantity ? ` · max ${a.max_quantity}` : ""}{!a.active ? " · hidden" : ""}</p>
                        </div>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => toggleProductAddon(a)}>
                          {a.active ? "Hide" : "Show"}
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs text-destructive hover:text-destructive" onClick={() => deleteProductAddon(a.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-[1fr_110px_100px_auto] gap-2 items-end">
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-widest">Name</Label>
                    <Input value={newAddon.name} onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })} placeholder="Throne chair" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-widest">Price</Label>
                    <Input type="number" step="0.01" value={newAddon.price} onChange={(e) => setNewAddon({ ...newAddon, price: e.target.value })} placeholder="65" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-widest">Max qty</Label>
                    <Input type="number" min="1" value={newAddon.max_quantity} onChange={(e) => setNewAddon({ ...newAddon, max_quantity: e.target.value })} placeholder="—" />
                  </div>
                  <Button size="sm" onClick={addProductAddon} className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {draft.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// ============ Phase C CRMs live in AdminExtras.tsx ============
export {
  AdminGallery, AdminTeam, AdminTestimonials,
  AdminSubscribers, AdminContact, AdminSettings,
} from "./AdminExtras";

export { AdminDashboard, AdminBookings, AdminBlog, AdminProducts };
